import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { SmallSecondaryButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';
import { fetchMyUsage, formatTokens, formatUsd, type MyUsage, type UsageTotals } from './aiUsageApi';

function Row({ label, day, month, render }: { label: string; day: UsageTotals; month: UsageTotals; render: (t: UsageTotals) => string }) {
  return (
    <tr className="border-t border-gray-100">
      <th scope="row" className="py-1.5 pr-4 text-left font-normal text-gray-600">{label}</th>
      <td className="py-1.5 pr-4 text-right tabular-nums">{render(day)}</td>
      <td className="py-1.5 text-right tabular-nums">{render(month)}</td>
    </tr>
  );
}

/**
 * Settings > Usage > "Your AI usage": what the signed-in user consumed today and this month (read in the company's time zone), in tokens by category and
 * as an ESTIMATED consumption in USD against the daily limit and (optional) monthly cap. It is an estimate, not an invoice: nothing is charged.
 */
export function MyAiUsageSection() {
  const [usage, setUsage] = useState<MyUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setUsage(await fetchMyUsage());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Your AI usage could not be loaded');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  return (
    <Card className="max-w-2xl" data-testid="my-ai-usage">
      <CardHeader><CardTitle className="font-heading-text">Your AI usage</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        {loading && <p className="text-sm text-gray-500">Loading your usage...</p>}
        {!loading && error && (
          <div className="space-y-2">
            <div className="error-message" role="alert">{error}</div>
            <SmallSecondaryButton type="button" onClick={() => void load()}>Retry</SmallSecondaryButton>
          </div>
        )}
        {!loading && !error && usage && !usage.available && <p className="text-sm text-gray-500">{usage.message}</p>}
        {!loading && !error && usage && usage.available && (
          <>
            <div data-testid="limits">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm font-medium text-gray-900" data-testid="daily-line">
                  Today: {formatUsd(usage.budget.day_used_usd ?? 0)} of {formatUsd(usage.budget.daily_limit_usd)} daily limit
                </span>
                <span className="text-sm text-gray-600">{usage.budget.day_percent ?? 0}% used</span>
              </div>
              <Progress
                value={usage.budget.day_percent ?? 0}
                className="bg-gray-100"
                indicatorColor={(usage.budget.day_percent ?? 0) >= 100 ? colors.tagStatusRejected : (usage.budget.day_percent ?? 0) >= 80 ? colors.tagStatusPending : colors.tagStatusActive}
              />
              {usage.budget.monthly_cap_usd !== null ? (
                <p className="mt-2 text-sm text-gray-500" data-testid="monthly-line">
                  This month: {formatUsd(usage.budget.month_used_usd ?? 0)} of {formatUsd(usage.budget.monthly_cap_usd)} monthly cap ({usage.budget.month_percent ?? 0}% used, {formatUsd(usage.budget.remaining_usd ?? 0)} remaining)
                </p>
              ) : (
                <p className="mt-2 text-sm text-gray-500" data-testid="monthly-line">No monthly cap.</p>
              )}
              {usage.budget.blocked && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  You have reached an AI limit, so new AI requests are paused. Rules and everything that does not use AI keep working. Ask an admin to raise it, or wait for it to reset.
                </div>
              )}
            </div>

            <table className="w-full text-sm" data-testid="my-usage-table">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="text-left font-normal pb-1" />
                  <th className="text-right font-normal pb-1 pr-4">Today</th>
                  <th className="text-right font-normal pb-1">This month</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Estimated consumption" day={usage.day} month={usage.month} render={(t) => formatUsd(t.weighted_usd)} />
                <Row label="Input tokens" day={usage.day} month={usage.month} render={(t) => formatTokens(t.input_tokens)} />
                <Row label="Output tokens" day={usage.day} month={usage.month} render={(t) => formatTokens(t.output_tokens)} />
              </tbody>
            </table>

            {usage.models.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-1">This month by model</p>
                <ul className="text-sm divide-y divide-gray-100" data-testid="my-usage-models">
                  {usage.models.map((m) => (
                    <li key={m.model} className="flex justify-between py-1.5"><span>{m.model}</span><span className="tabular-nums">{formatUsd(m.weighted_usd)}</span></li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500">Estimated consumption.</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
